import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useLayoutEffect,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Box,
  TextField,
  InputAdornment,
  Paper,
  ClickAwayListener,
  Chip,
  Tooltip,
  Skeleton,
  Typography,
  IconButton,
  Popper,
  Fade,
  useTheme,
} from "@mui/material";
import {
  useProjects,
  validateSelectedProjects,
} from "../contexts/ProjectContext";
import { useThemeMode } from "../contexts/ThemeContext";
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Folder as FolderIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Clear as ClearIcon,
  MoreHoriz as MoreHorizIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import CreateProjectForm from "./CreateProjectForm";
import { useStoredTheme } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";
import {
  notifyProjectSelectionChanged,
  PROJECT_SELECTION_CHANGED_EVENT,
} from "../utils/project-selection-notifier";
import {
  useProjectsQuery,
  useProjectsQueryScope,
} from "../features/projects/hooks/useProjectsQuery";
const MAX_CHIPS_DISPLAY = 2; // Maximum number of chips to display before showing "more"

export { PROJECT_SELECTION_CHANGED_EVENT };

const ProjectSwitcher = () => {
  const { t } = useTranslation();
  const storedTheme = useStoredTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedProjects,
    toggleProject,
    removeProject,
  } = useProjects();
  const {
    data: availableProjects,
    error,
    isFetching,
    isPending,
    refetch,
  } = useProjectsQuery();

  // Use utility function to ensure we always have valid arrays with stable references
  const safeSelectedProjects = useMemo(
    () => validateSelectedProjects(selectedProjects),
    [selectedProjects],
  );
  const safeAvailableProjects = useMemo(
    () =>
      Array.isArray(availableProjects)
        ? Array.from(
            new Map(
              availableProjects.map((project) => [String(project.id), project]),
            ).values(),
          )
        : [],
    [availableProjects],
  );
  const loading = isPending || isFetching;
  const projectNames = useMemo(
    () =>
      new Map(
        safeAvailableProjects.map((project) => [
          String(project.id),
          project.name,
        ]),
      ),
    [safeAvailableProjects],
  );
  const getProjectName = useCallback(
    (projectId) => projectNames.get(String(projectId)) ?? "Unknown Project",
    [projectNames],
  );
  const [isOpen, setIsOpen] = useState(false);
  const { scope } = useProjectsQueryScope();
  const scopeKey = JSON.stringify([scope.principalId, scope.organizationId]);
  const showSearch =
    new Set(safeAvailableProjects.map((project) => String(project.id))).size >= 6;
  const [search, setSearch] = useState({ scopeKey, value: "" });
  if (search.scopeKey !== scopeKey || (!showSearch && search.value)) {
    setSearch({ scopeKey, value: "" });
  }
  const searchQuery =
    showSearch && search.scopeKey === scopeKey ? search.value : "";
  const setSearchQuery = (value) => setSearch({ scopeKey, value });
  const menuRef = useRef(null);
  const restoreTrigger = useRef(false);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const buttonRef = useRef(null);
  const projectTriggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(280);
  const prevSelectedProjectsRef = useRef(safeSelectedProjects);
  const [showGuideTooltip, setShowGuideTooltip] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { mode } = useThemeMode();
  const theme = useTheme();

  // Update input width when dropdown opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      // Make input width match button width exactly, no extra padding
      const buttonWidth = buttonRef.current.offsetWidth;
      setInputWidth(buttonWidth);
    }
  }, [isOpen]);

  // Monitor changes to selectedProjects and dispatch custom event when they change
  useEffect(() => {
    notifyProjectSelectionChanged({
      previousSelectionRef: prevSelectedProjectsRef,
      selectedProjects: safeSelectedProjects,
      pathname: location.pathname,
    });
  }, [safeSelectedProjects, location.pathname]);

  // Show tooltip if no projects are available after loading completes
  useEffect(() => {
    if (!loading && safeAvailableProjects.length === 0 && !createFormOpen) {
      // Only show tooltip after a short delay to avoid it showing immediately on first load
      const timer = setTimeout(() => {
        setShowGuideTooltip(true);
        setTooltipOpen(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowGuideTooltip(false);
      setTooltipOpen(false);
    }
  }, [loading, safeAvailableProjects.length, createFormOpen]);

  // Auto-hide tooltip after 8 seconds
  useEffect(() => {
    if (tooltipOpen) {
      const timer = setTimeout(() => {
        setTooltipOpen(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [tooltipOpen]);

  const handleClick = (event) => {
    setIsOpen(true);
  };

  useLayoutEffect(() => {
    if (isOpen && !menuRef.current?.contains(document.activeElement)) {
      const target = showSearch
        ? searchInputRef.current
        : menuRef.current?.querySelector('[data-project-option]');
      target?.focus();
    } else if (!isOpen && restoreTrigger.current) {
      restoreTrigger.current = false;
      if (projectTriggerRef.current?.isConnected && !projectTriggerRef.current.disabled) {
        projectTriggerRef.current.focus();
      }
    }
  }, [isOpen, showSearch]);

  const handleClose = (restoreFocus = true) => {
    restoreTrigger.current = restoreFocus;
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleProjectToggle = useCallback(
    (projectId) => {
      toggleProject(projectId);
    },
    [toggleProject],
  );

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClickAway = (event) => {
    // Don't close if clicking on the search input
    if (
      searchInputRef.current &&
      searchInputRef.current.contains(event.target)
    ) {
      return;
    }
    handleClose(false);
  };

  const handleRemoveProject = (event, projectId) => {
    event.stopPropagation(); // Prevent the dropdown from opening
    removeProject(projectId);
  };

  const handleCreateProjectClick = () => {
    setSearchQuery("");
    setCreateFormOpen(true);
    setIsOpen(false); // Close the dropdown
  };

  const handleCreateFormClose = () => {
    setCreateFormOpen(false);
  };

  const handleManageProjectsClick = () => {
    setSearchQuery("");
    setIsOpen(false); // Close the dropdown
    navigate("/projects"); // Navigate to projects management page
  };

  const handleRefreshProjects = (e) => {
    e.stopPropagation();
    void refetch();
  };

  const filteredProjects = safeAvailableProjects.filter(
    (project) =>
      project?.name &&
      typeof project.name === "string" &&
      project.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Get button content based on selection
  const getButtonContent = () => {
    // Show loading skeleton when projects are loading
    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <FolderIcon sx={{ color: "action.disabled" }} />
          <Skeleton variant="text" width={120} sx={{ marginInlineStart: 1 }} />
        </Box>
      );
    }

    // Show error state if there was an error loading projects
    if (error) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            color: "error.main",
          }}
        >
          <FolderIcon sx={{ color: "error.main" }} />
          <Box
            sx={{ marginInlineStart: 1, display: "flex", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ marginInlineEnd: 1 }}>
              {t('projects.errorLoadingProjects')}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (safeSelectedProjects.includes("all")) {
      return (
        <>
          <FolderIcon />
          <Box sx={{ marginInlineStart: 1 }}>{t('projects.allProjects')}</Box>
        </>
      );
    } else if (safeSelectedProjects.length === 1) {
      // Single project selected
      const projectName = getProjectName(safeSelectedProjects[0]);
      // Only show the project if we have its data, otherwise show a fallback
      return (
        <>
          <FolderIcon />
          <Box
            sx={{
              marginInlineStart: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {projectName !== "Unknown Project"
              ? projectName
              : t('projects.loadingProject')}
          </Box>
        </>
      );
    } else {
      // Multiple projects selected - show chips with limit
      const displayProjects = safeSelectedProjects.slice(0, MAX_CHIPS_DISPLAY);
      const extraCount = safeSelectedProjects.length - MAX_CHIPS_DISPLAY;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <FolderIcon sx={{ marginInlineEnd: 1, flexShrink: 0 }} />
          <Box sx={{ display: "flex", flexWrap: "nowrap", overflow: "hidden" }}>
            {displayProjects.map((projectId) => {
              const projectName = getProjectName(projectId);
              // Skip rendering chips for unknown projects during loading
              if (projectName === "Unknown Project" && loading) {
                return (
                  <Skeleton
                    key={projectId}
                    variant="rounded"
                    width={80}
                    height={24}
                    sx={{ marginInlineEnd: 0.5 }}
                  />
                );
              }

              return (
                <Chip
                  key={projectId}
                  size="small"
                  label={
                    projectName !== "Unknown Project"
                      ? projectName
                      : t('common.loading')
                  }
                  onDelete={(e) => handleRemoveProject(e, projectId)}
                  deleteIcon={<ClearIcon fontSize="small" />}
                  sx={{
                    marginInlineEnd: 0.5,
                    height: 24,
                    maxWidth: 120,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              );
            })}
            {extraCount > 0 && (
              <Tooltip
                title={
                  loading
                    ? t('projects.loadingProjects')
                    : safeSelectedProjects
                        .slice(MAX_CHIPS_DISPLAY)
                        .map((id) => getProjectName(id))
                        .join(", ")
                }
              >
                <Chip
                  size="small"
                  label={t('projects.moreCount', { count: extraCount })}
                  icon={<MoreHorizIcon fontSize="small" />}
                  sx={{ height: 24 }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      );
    }
  };

  const currentOrganizationName = localStorage.getItem(
    "currentOrganizationName",
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative" }}>
          {showGuideTooltip && (
            <Popper
              open={tooltipOpen}
              anchorEl={buttonRef.current}
              placement="bottom-start"
              transition
              disablePortal
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
              sx={{ zIndex: 1200 }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      maxWidth: 280,
                      borderRadius: 1,
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -10,
                        insetInlineStart: 20,
                        borderWidth: "0 10px 10px",
                        borderStyle: "solid",
                        borderColor: "transparent transparent",
                        borderBottomColor: storedTheme.card.borderColor[mode],
                        zIndex: 0,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: -9,
                        insetInlineStart: 20,
                        borderWidth: "0 10px 10px",
                        borderStyle: "solid",
                        borderColor: "transparent transparent",
                        borderBottomColor:
                          storedTheme.card.backgroundColor[mode],
                        zIndex: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mb: 1 }}
                    >
                      {t('projects.createFirstProject')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1.5,
                        fontSize: "0.85rem",
                        opacity: mode === "light" ? 0.95 : 0.8,
                      }}
                    >
                      {t('projects.projectHelpText')}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={handleCreateProjectClick}
                      startIcon={<AddIcon fontSize="small" />}
                      sx={{
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        py: 0.5,
                        px: 1.5,
                        minWidth: "auto",
                        height: 28,
                        fontWeight: 500,
                        "& .MuiButton-startIcon": {
                          marginInlineEnd: 0.5,
                        },
                      }}
                    >
                      {t('projects.createProject')}
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => setTooltipOpen(false)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        insetInlineEnd: 8,
                        opacity: 0.7,
                        padding: 0.5,
                        fontSize: "0.75rem",
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Paper>
                </Fade>
              )}
            </Popper>
          )}

          <Box ref={buttonRef}>
            <Button
              ref={projectTriggerRef}
              variant="outlined"
              onClick={() => isOpen ? handleClose() : handleClick()}
              aria-expanded={isOpen}
              aria-haspopup="menu"
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                color: "text.primary",
                borderColor: "divider",
                borderRadius: 0.5,
                px: 2,
                py: 1,
                display: "flex",
                justifyContent: "flex-start",
                textAlign: "start",
                maxWidth: 320,
                minWidth: 180, // Add minimum width to prevent button jumping during loading
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                  color: "primary.main",
                },
                "& .MuiButton-endIcon": {
                  marginInlineStart: "auto",
                },
                "& .MuiSvgIcon-root": {
                  fontSize: "1.2rem",
                },
              }}
              disabled={loading} // Disable button during loading
            >
              {getButtonContent()}
            </Button>
            {error && <IconButton size="small" aria-label={t('common.retry')} onClick={handleRefreshProjects}><RefreshIcon /></IconButton>}
          </Box>

          {isOpen && (
            <Paper
              elevation={1}
              ref={menuRef}
              role="menu"
              aria-label={t('projects.allProjects')}
              onKeyDown={(event) => {
                if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); handleClose(); return; }
                const editable = event.target instanceof Element
                  ? event.target.closest("input, textarea, [contenteditable]")
                  : null;
                const isEditable = editable instanceof HTMLElement && (
                  editable.matches("input, textarea") || editable.isContentEditable ||
                  ["", "true", "plaintext-only"].includes(editable.getAttribute("contenteditable")?.toLowerCase() ?? "false")
                );
                if (isEditable && ["Home", "End"].includes(event.key)) return;
                if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
                const options = Array.from(menuRef.current.querySelectorAll('[role^="menuitem"]'));
                const index = options.indexOf(document.activeElement);
                const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : index < 0 ? (event.key === "ArrowDown" ? 0 : options.length - 1) : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
                event.preventDefault(); options[next]?.focus();
              }}
              sx={{
                position: "absolute",
                top: "100%",
                insetInlineStart: 0,
                insetInlineEnd: 0,
                width: inputWidth,
                maxWidth: "calc(100vw - 16px)",
                zIndex: 1300,
                mt: 0.5,
                borderRadius: 0.5,
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {showSearch && (
                <Box sx={{ p: 1 }}>
                  <TextField
                    inputRef={searchInputRef}
                    inputProps={{ "aria-label": t("projects.searchProjects") }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") { event.preventDefault(); handleClose(); }
                      if (event.key === "ArrowDown") { event.preventDefault(); event.stopPropagation(); menuRef.current?.querySelector('[data-project-option]')?.focus(); }
                    }}
                    variant="outlined"
                    placeholder={t('projects.searchProjects')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FolderIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {searchQuery && <IconButton size="small" aria-label={`${t('common.reset')} ${t('projects.searchProjects')}`} onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}><ClearIcon /></IconButton>}
                          <IconButton size="small" aria-label={t('common.close')} onClick={() => handleClose()}><KeyboardArrowUpIcon /></IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0.5,
                        height: 40, // More compact height
                        padding: "0 8px", // Reduce horizontal padding
                        "& fieldset": {
                          borderColor: "divider",
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: "1px",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 0", // Reduce vertical padding
                        fontSize: "0.875rem", // Slightly smaller font
                      },
                      "& .MuiInputAdornment-root": {
                        marginInlineEnd: 0, // Reduce spacing for adornments
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.2rem", // Smaller icon size
                        },
                      },
                    }}
                  />
                </Box>
              )}
              {/* Organization Context Section */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.02)"
                      : "rgba(0, 0, 0, 0.01)",
                }}
              >
                {currentOrganizationName ? (
                  <Tooltip
                    title={t('organizations.switchOrganizationTooltip')}
                    placement="bottom"
                    arrow
                  >
                    <Box
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.currentTarget.click(); } }}
                      onClick={() => {
                        setSearchQuery("");
                        navigate("/settings?tab=organizations");
                        setIsOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(19, 211, 158, 0.08)"
                              : "rgba(19, 211, 158, 0.04)",
                        },
                      }}
                    >
                      <BusinessIcon
                        sx={{
                          color: "primary.main",
                          fontSize: "1.1rem",
                          marginInlineEnd: 1.5,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          {t('organizations.organizationProjects')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "primary.main",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            lineHeight: 1.2,
                          }}
                        >
                          {currentOrganizationName}
                        </Typography>
                      </Box>
                      <SwapHorizIcon
                        sx={{
                          color: "text.secondary",
                          fontSize: "1rem",
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={t('organizations.joinOrganizationTooltip')}
                    placement="bottom"
                    arrow
                  >
                    <Box
                      role="menuitem"
                      tabIndex={0}
                      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.currentTarget.click(); } }}
                      onClick={() => {
                        setSearchQuery("");
                        navigate("/settings?tab=organizations");
                        setIsOpen(false);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        p: 1,
                        borderRadius: 1,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(19, 211, 158, 0.08)"
                              : "rgba(19, 211, 158, 0.04)",
                        },
                      }}
                    >
                      <PersonIcon
                        sx={{
                          color: "text.secondary",
                          fontSize: "1.1rem",
                          marginInlineEnd: 1.5,
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                            display: "block",
                            lineHeight: 1,
                          }}
                        >
                          {t('projects.personalProjects')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            fontStyle: "italic",
                          }}
                        >
                          {t('organizations.clickToJoin')}
                        </Typography>
                      </Box>
                      <AddIcon
                        sx={{
                          color: "primary.main",
                          fontSize: "1rem",
                          opacity: 0.8,
                        }}
                      />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <MenuItem
                data-project-option
                role="menuitemcheckbox"
                aria-checked={safeSelectedProjects.includes("all")}
                tabIndex={0}
                onClick={() => handleProjectToggle("all")}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    inputProps={{ "aria-hidden": true }}
                    edge="start"
                    checked={safeSelectedProjects.includes("all")}
                    tabIndex={-1}
                    disableRipple
                    sx={{
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                      padding: 0.5,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.allProjects')}
                  primaryTypographyProps={{
                    fontWeight: safeSelectedProjects.includes("all")
                      ? 600
                      : 400,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>
              <Divider sx={{ my: 0.5, opacity: 0.7 }} />

              <Box data-project-scroll sx={{ maxHeight: 200, overflowY: "auto", overflowX: "hidden" }}>
                {loading ? (
                  // Show skeleton loaders during loading instead of just a spinner
                  <Box sx={{ px: 1.5, py: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((item) => (
                      <Box
                        key={item}
                        sx={{ display: "flex", alignItems: "center", my: 1 }}
                      >
                        <Skeleton
                          variant="circular"
                          width={20}
                          height={20}
                          sx={{ marginInlineEnd: 1.5 }}
                        />
                        <Skeleton
                          variant="text"
                          width={Math.floor(Math.random() * 40) + 80}
                          height={20}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : filteredProjects.length > 0 ? (
                  filteredProjects.map((project) => (
                    <MenuItem
                      key={project.id}
                      data-project-option
                      role="menuitemcheckbox"
                      aria-checked={safeSelectedProjects.includes(project.id)}
                      tabIndex={0}
                      onClick={() => handleProjectToggle(project.id)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Checkbox
                          inputProps={{ "aria-hidden": true }}
                          edge="start"
                          checked={safeSelectedProjects.includes(project.id)}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            "&.Mui-checked": {
                              color: "primary.main",
                            },
                            padding: 0.5,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        primaryTypographyProps={{
                          sx: { whiteSpace: "normal", overflowWrap: "anywhere" },
                          fontWeight: safeSelectedProjects.includes(project.id)
                            ? 600
                            : 400,
                          fontSize: "0.9rem",
                        }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <Box
                    sx={{
                      py: 2,
                      px: 1.5,
                      color: "text.secondary",
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    {searchQuery ? (
                      t('projects.noMatchingProjects')
                    ) : (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {t('projects.noProjectsAvailable')}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={handleCreateProjectClick}
                            sx={{
                              mt: 0.5,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              borderColor: "primary.main",
                              color: "primary.main",
                              py: 0.5,
                              px: 1,
                              minWidth: "auto",
                              height: 26,
                              "& .MuiButton-startIcon": {
                                marginInlineEnd: 0.5,
                              },
                            }}
                          >
                            {t('projects.createProject')}
                          </Button>
                          <Button
                            size="small"
                            variant="text"
                            startIcon={
                              <RefreshIcon
                                className="rtl:scale-x-[-1]"
                                fontSize="small"
                              />
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              void refetch();
                            }}
                            sx={{
                              mt: 0.5,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              py: 0.5,
                              px: 1,
                              minWidth: "auto",
                              height: 26,
                              "& .MuiButton-startIcon": {
                                marginInlineEnd: 0.5,
                              },
                            }}
                          >
                            {t('common.retry')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0.5, opacity: 0.7 }} />

              <MenuItem
                onClick={handleCreateProjectClick}
                tabIndex={0}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AddIcon fontSize="small" sx={{ color: "primary.main" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.createNewProject')}
                  primaryTypographyProps={{
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={handleManageProjectsClick}
                tabIndex={0}
                sx={{
                  py: 1,
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <SettingsIcon
                    fontSize="small"
                    sx={{ color: "primary.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('projects.manageProjects')}
                  primaryTypographyProps={{
                    color: "primary.main",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                  }}
                />
              </MenuItem>
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {/* Create Project Form Dialog */}
      <CreateProjectForm
        open={createFormOpen}
        onClose={handleCreateFormClose}
        returnFocusRef={projectTriggerRef}
      />
    </>
  );
};

export default ProjectSwitcher;
